<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { projectsApi } from '$lib/api/projects';
  import { quotationsApi } from '$lib/api/quotations';
  import { getErrorMessage } from '$lib/api/client';
  import { route } from '$lib/state/route.svelte';
  import { session } from '$lib/state/session.svelte';
  import { QUOTE_VERTICAL_META, type Project } from '$lib/types';
  import Pagination from '$lib/components/ui/Pagination.svelte';
  import ProjectModal from './ProjectModal.svelte';
  import NewQuoteModal from '$lib/components/pipeline/NewQuoteModal.svelte';

  const selected = $derived(route.current.tab === 'projects' ? route.current.id : undefined);
  const projects = createQuery(() => ({ queryKey: ['projects'], queryFn: projectsApi.all }));
  const project = $derived(projects.data?.find((p) => p.id === selected));
  let search = $state('');
  let page = $state(1);
  let quoteSearch = $state('');
  let quotePage = $state(1);
  let archived = $state(false);
  let editing = $state<Project | null | undefined>(undefined);
  let newQuote = $state(false);
  const perPage = 20;
  const filtered = $derived((projects.data ?? []).filter((p) => `${p.name} ${p.client_name} ${p.location}`.toLowerCase().includes(search.toLowerCase())));
  $effect(() => { search; page = 1; });
  $effect(() => { selected; quoteSearch; archived; quotePage = 1; });
  const quotes = createQuery(() => ({
    queryKey: ['project-quotations', selected, quoteSearch, quotePage, archived],
    queryFn: () => quotationsApi.list({ project_id: selected, q: quoteSearch, page: quotePage, per_page: perPage, archived: archived ? '1' : undefined }),
    enabled: selected !== undefined
  }));
</script>

<div class="pq-scroll flex-1 overflow-y-auto px-4 py-4 lg:px-7 lg:py-5">
  <div class="max-w-[1100px] mx-auto flex flex-col gap-4">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div><h1 class="font-heading text-xl font-bold text-navy">{selected === 'none' ? 'Ungrouped quotations' : selected ? project?.name ?? `Project #${selected}` : 'Projects'}</h1>
        <p class="text-sm text-dark-gray">{selected && project ? [project.client_name, project.location].filter(Boolean).join(' · ') : 'Optional customer / site groups. Independent of quotation pricing and export workflow.'}</p>
      </div>
      <div class="flex flex-wrap gap-2">
        {#if selected !== undefined}
          <button class="btn-secondary" onclick={() => route.push({ tab: 'projects' })}>All projects</button>
          {#if project && (session.isAdmin || project.created_by_id === session.me?.user.id)}<button class="btn-secondary" onclick={() => (editing = project)}>Edit project</button>{/if}
          {#if typeof selected === 'number'}<button class="btn-primary" onclick={() => (newQuote = true)}>New quotation in project</button>{/if}
        {:else}
          <button class="btn-secondary" onclick={() => route.push({ tab: 'projects', id: 'none' })}>Ungrouped quotations</button>
          <button class="btn-primary" onclick={() => (editing = null)}>Create project</button>
        {/if}
      </div>
    </div>
    {#if selected === undefined}
      <input class="input w-full" aria-label="Search projects" placeholder="Search projects, customers or sites…" bind:value={search} />
      {#if projects.isLoading}<p class="tym-loading-state">Loading projects…</p>
      {:else if projects.isError}<p class="tym-error-state">{getErrorMessage(projects.error)} <button class="btn-secondary" onclick={() => projects.refetch()}>Retry</button></p>
      {:else}
        <div class="tym-card tym-table-wrap tym-table-scroll"><table class="tym-table">
          <thead><tr><th>Project</th><th>Customer</th><th>Site / location</th></tr></thead>
          <tbody>{#each filtered.slice((page - 1) * perPage, page * perPage) as p (p.id)}
            <tr><td><button class="text-toyama-teal font-medium text-left" onclick={() => route.push({ tab: 'projects', id: p.id })}>{p.name}</button></td><td>{p.client_name || '—'}</td><td>{p.location || '—'}</td></tr>
          {:else}<tr><td class="empty" colspan="3">No projects found. Quotations do not require a project.</td></tr>{/each}</tbody>
        </table></div>
        <Pagination {page} {perPage} count={filtered.length} onChange={(v) => (page = v)} />
      {/if}
    {:else}
      <div class="flex flex-wrap gap-3 items-center">
        <input class="input flex-1" aria-label="Search project quotations" placeholder="Search quotations…" bind:value={quoteSearch} />
        <label class="text-sm text-dark-gray flex items-center gap-2"><input type="checkbox" bind:checked={archived} />Archived quotations</label>
      </div>
      <p class="text-xs text-dark-gray">{archived ? 'Archived' : 'Active'} quotations across all statuses and accessible verticals.</p>
      {#if quotes.isLoading}<p class="tym-loading-state">Loading quotations…</p>
      {:else if quotes.isError}<p class="tym-error-state">{getErrorMessage(quotes.error)} <button class="btn-secondary" onclick={() => quotes.refetch()}>Retry</button></p>
      {:else}
        <div class="tym-card tym-table-wrap tym-table-scroll"><table class="tym-table">
          <thead><tr><th>Number</th><th>Client</th><th>Quotation title</th><th>Vertical</th><th>Status</th></tr></thead>
          <tbody>{#each quotes.data?.results ?? [] as q (q.id)}
            <tr><td><button class="text-toyama-teal font-mono text-left" onclick={() => route.push({ tab: 'quotations', id: q.id })}>{q.formatted_number}</button></td><td>{q.client_name || '—'}</td><td>{q.project_name || 'Untitled'}</td><td>{QUOTE_VERTICAL_META[q.vertical]?.label ?? q.vertical}</td><td class="capitalize">{q.status}{q.archived_at ? ' · archived' : ''}</td></tr>
          {:else}<tr><td class="empty" colspan="5">No {archived ? 'archived' : 'active'} quotations found.</td></tr>{/each}</tbody>
        </table></div>
        <Pagination page={quotePage} {perPage} count={quotes.data?.count ?? 0} onChange={(v) => (quotePage = v)} />
      {/if}
    {/if}
  </div>
</div>
{#if editing !== undefined}<ProjectModal project={editing ?? undefined} onClose={() => (editing = undefined)} onSaved={(p) => { editing = undefined; route.push({ tab: 'projects', id: p.id }); }} />{/if}
{#if newQuote && typeof selected === 'number'}<NewQuoteModal projectId={selected} onClose={() => (newQuote = false)} onCreated={(q) => { newQuote = false; route.push({ tab: 'quotations', id: q.id }); }} />{/if}
